import type { FC } from 'react';
import { Tabs } from 'antd';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { addTag, removeTag, setActiveTag } from '../../../stores/tags-view.store';

import TagsViewAction from './tagViewAction';
import ReactGA from 'react-ga4';

const labelMap: Record<string, string> = {
  users: 'User',
  admins: 'Admin',
  companies: 'Company',
  vehicles: 'Vehicle',
  devices: 'Device',
  orders: 'Order',
  drivers: 'Driver',
  makes: 'Make',
  models: 'Model',
  logbook: 'Logbook',

  'top-errors': 'Top Error',
  'company-errors': 'Company Error',

  'check-stripe': 'Stripe Check',
  provider: 'Provider',

  'disabled-companies': 'Disabled Company',
  'disputed-companies': 'Disputed Company',
};

const TagsView: FC = () => {
  const { tags, activeTagId } = useSelector((state: any) => state.tagsView);
  const { menuList, locale } = useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // onClick tag
  const onChange = (key: string) => {
    const tag = tags.find((tag: any) => tag.path === key);
    if (tag) {
      setCurrentTag(tag.path);

      // Восстановить query параметры из tag.queryParams
      const query = tag.queryParams ? `?${new URLSearchParams(tag.queryParams).toString()}` : '';
      navigate(`${tag.path}${query}`);
    }
  };

  // onRemove tag
  const onClose = (targetKey: string) => {
    dispatch(removeTag(targetKey));
  };

  const setCurrentTag = useCallback(
    (id?: string) => {
      const tag = tags.find((item: any) => {
        if (id) {
          return item.path === id;
        } else {
          return item.path === location.pathname;
        }
      });

      if (tag) {
        dispatch(setActiveTag(tag.path));
      }
    },
    [dispatch, location.pathname, tags],
  );

  useEffect(() => {
    if (menuList.length) {
      const menu = menuList.find((m: any) => m.path === location.pathname);
      ReactGA.send({ hitType: 'pageview', page: location.pathname });

      if (menu) {
        dispatch(
          addTag({
            ...menu,
            // closable: menu.code !== 'dashboard',
          }),
        );
      } else {
        const match = location.pathname.match(
          /^\/(user\/(users|admins|companies|vehicles|devices|orders|drivers|makes|models|logbook)|errors\/(top-errors|company-errors)|team\/(check-stripe|provider)|disabled-companies|disputed-companies)\/(\d+)$/,
        );

        if (match) {
          const [, , userType, errorType, teamType, id] = match;
          const type = userType || errorType || teamType || match[1]; // fallback for single segment types
          const label = labelMap[type] || 'Detail';
          dispatch(
            addTag({
              path: location.pathname,
              label: {
                en_US: `${label} #${id}`,
                zh_CN: `${label} #${id}`,
              },
              closable: true,
              code: 'dynamic-detail',
            }),
          );
        }
      }
    }
  }, [dispatch, location.pathname, menuList]);

  return (
    <div id="pageTabs" style={{ padding: '0px 0px 6px 0px' }}>
      <Tabs
        tabBarStyle={{ margin: 0 }}
        onChange={onChange}
        activeKey={activeTagId}
        type="editable-card"
        hideAdd
        onEdit={(targetKey, action) => action === 'remove' && onClose(targetKey as string)}
        tabBarExtraContent={<TagsViewAction />}
        items={tags.map((tag: any) => {
          return {
            key: tag.path,
            closable: tag.closable,
            label: tag.label[locale],
          };
        })}
      />
    </div>
  );
};

export default TagsView;
